import { useIntl } from "react-intl";
import Messages from "../localization/Messages";
import { DateFormat, getDateText } from "../helpers/dateHelper";
import { useCallback } from "react";

type CsvHeaderKey = (typeof Messages)[keyof typeof Messages];

export const delimiterKeys: Record<"tab" | "comma", string> = {
    tab: "\t",
    comma: ",",
};

function escapeCell(value: unknown) {
    const s = value == null ? "" : String(value);
    return `"${s.replace(/"/g, '""').replace(/\r?\n/g, "\r\n")}"`;
}

function normalizeRows(values: string[][], delimiterChar: string): string[][] {
    return values.map(row => {
        if (row.length === 1 && row[0].includes(delimiterChar)) {
            return row[0]
                .split(delimiterChar)
                .map(v => v.replace(/^"(.*)"$/, "$1"))
                .map(v => v.replace(/""/g, `"`));
        }
        return row;
    });
}

export function useCustomExportToCsv() {
    const intl = useIntl();

    const downloadFn = useCallback(
        (
            columns: CsvHeaderKey[],
            values: string[][],
            additionalColumns?: string[],
            fileName?: string,
            delimiter?: "tab" | "comma",
        ) => {
            const delimiterChar = delimiterKeys[delimiter ?? "comma"];

            const headerLabels = columns.map(message => intl.formatMessage(message));
            if (additionalColumns?.length) headerLabels.push(...additionalColumns);

            const localFileName =
                fileName ?? `export-${getDateText(Date.now(), DateFormat.API_DATE)}.csv`;

            const normalizedValues = normalizeRows(values, delimiterChar);

            const csvArray = [headerLabels, ...normalizedValues];

            const csvText =
                "\ufeff" + // UTF-8 BOM for Excel
                csvArray.map(row => row.map(escapeCell).join(delimiterChar)).join("\r\n");

            const blob = new Blob([csvText], {
                type: "text/csv;charset=utf-8",
            });

            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = localFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        },
        [intl],
    );

    return downloadFn;
}
